import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';

import HasPermission from '@/components/Middlewares/HasPermission';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

import { PageOverview } from './Modal';

const Sep = styled.div`
  height: 15px;
  width: 1px;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const Checkmark = styled.svg``;

interface ButtonProps {
  $isActive: boolean;
  $activeColor: string;
}

const Button = styled.button<ButtonProps>`
  height: 32px;
  width: 94px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.black};
    ${Sep} {
      background: ${({ theme }) => theme.colors.gray};
    }
  }

  ${({ $isActive, $activeColor }) =>
    $isActive &&
    css`
      color: ${$activeColor} !important;
      ${Checkmark} {
        color: ${$activeColor};
      }
    `}
`;

const Inner = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

interface Props {
  page: PageOverview;
  network:
    | EditorialFacebookPostNetwork.PINTEREST
    | EditorialFacebookPostNetwork.THREADS
    | EditorialFacebookPostNetwork.TWITTER
    | EditorialFacebookPostNetwork.LINKEDIN
    | EditorialFacebookPostNetwork.TIKTOK;
}

const onAuthorizeThreads = (pageId: number) =>
  (window.location.href = `${process.env.REACT_APP_API_ENDPOINT!}/meta-threads/authorize?channel_id=${pageId}`);

const onAuthorizePinterest = (pageId: number) =>
  (window.location.href = `${process.env.REACT_APP_API_ENDPOINT!}/pinterest/authorize?channel_id=${pageId}`);

const onAuthorizeTwitter = (pageId: number) =>
  (window.location.href = `${process.env.REACT_APP_API_ENDPOINT!}/twitter/authorize?channel_id=${pageId}`);

const onAuthorizeLinkedIn = (pageId: number) =>
  (window.location.href = `${process.env.REACT_APP_API_ENDPOINT!}/linkedin/authorize?channel_id=${pageId}`);

const onAuthorizeTiktok = (pageId: number) =>
  (window.location.href = `${process.env.REACT_APP_API_ENDPOINT!}/tiktok/authorize?channel_id=${pageId}`);

const AuthorizationButton: FC<Props> = ({ page, network }) => {
  const { isActive, activeColor } = useMemo(() => {
    if (network === EditorialFacebookPostNetwork.PINTEREST) {
      return {
        isActive: page.hasPinterest,
        activeColor: '#E60023',
      };
    }

    if (network === EditorialFacebookPostNetwork.THREADS) {
      return {
        isActive: page.hasThreads,
        activeColor: '#000000',
      };
    }

    if (network === EditorialFacebookPostNetwork.LINKEDIN) {
      return {
        isActive: page.hasLinkedin,
        activeColor: '#0077B5',
      };
    }

    if (network === EditorialFacebookPostNetwork.TIKTOK) {
      return {
        isActive: page.hasTiktok,
        activeColor: '#000000',
      };
    }

    return {
      isActive: page.hasTwitter,
      activeColor: '#000000',
    };
  }, [page, network]);

  const onClick = () => {
    if (network === EditorialFacebookPostNetwork.THREADS) {
      return onAuthorizeThreads(page.id);
    }

    if (network === EditorialFacebookPostNetwork.PINTEREST) {
      return onAuthorizePinterest(page.id);
    }

    if (network === EditorialFacebookPostNetwork.LINKEDIN) {
      return onAuthorizeLinkedIn(page.id);
    }

    if (network === EditorialFacebookPostNetwork.TIKTOK) {
      return onAuthorizeTiktok(page.id);
    }

    return onAuthorizeTwitter(page.id);
  };

  return (
    <HasPermission permissions={['authorize distribution queue']}>
      <Button
        $isActive={isActive}
        $activeColor={activeColor}
        onClick={onClick}
        type="button"
      >
        <Inner>
          {network === EditorialFacebookPostNetwork.PINTEREST && (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_13336_70098)">
                <path
                  d="M0 8.00012C0 11.0712 1.84676 13.7096 4.48939 14.8696C4.4683 14.3459 4.48564 13.7172 4.61994 13.1473C4.76414 12.5385 5.58498 9.06055 5.58498 9.06055C5.58498 9.06055 5.34539 8.58166 5.34539 7.87391C5.34539 6.76244 5.98957 5.93234 6.79184 5.93234C7.47404 5.93234 7.80363 6.44475 7.80363 7.05834C7.80363 7.74412 7.36623 8.76992 7.14129 9.72002C6.95338 10.5156 7.54019 11.1645 8.32506 11.1645C9.74607 11.1645 10.7031 9.33939 10.7031 7.17693C10.7031 5.53314 9.59602 4.30279 7.58232 4.30279C5.30725 4.30279 3.88992 5.99943 3.88992 7.89459C3.88992 8.54803 4.08258 9.00881 4.38434 9.36564C4.52309 9.52953 4.54236 9.59545 4.49215 9.78365C4.45617 9.92164 4.37355 10.2539 4.33934 10.3855C4.28941 10.5755 4.13549 10.6434 3.96381 10.5733C2.91592 10.1455 2.42789 8.99791 2.42789 7.70791C2.42789 5.57738 4.22473 3.0227 7.78816 3.0227C10.6516 3.0227 12.5363 5.0948 12.5363 7.31908C12.5363 10.2612 10.9006 12.4593 8.48947 12.4593C7.67977 12.4593 6.91811 12.0216 6.65719 11.5244C6.65719 11.5244 6.22178 13.2525 6.12955 13.5862C5.97053 14.1644 5.65928 14.7424 5.37469 15.1929C6.06478 15.3969 6.78067 15.5005 7.50029 15.5004C11.6419 15.5004 15.0001 12.1425 15.0001 8.00012C15.0001 3.85795 11.6419 0.5 7.50029 0.5C3.35824 0.5 0 3.85795 0 8.00012Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_13336_70098">
                  <rect
                    width="15"
                    height="15"
                    fill="white"
                    transform="translate(0 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
          )}
          {network === EditorialFacebookPostNetwork.THREADS && (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.8853 7.36286C10.7696 5.22929 9.60387 4.00786 7.64673 3.99571C6.46673 3.98857 5.4803 4.48857 4.87601 5.405L5.95316 6.14286C6.40602 5.45643 7.12102 5.31429 7.63887 5.31929C8.28387 5.32357 8.76959 5.51071 9.08459 5.87643C9.31316 6.14214 9.46673 6.51 9.54316 6.97357C8.97173 6.87643 8.35316 6.84643 7.69316 6.88429C5.83173 6.99143 4.63601 8.07714 4.71602 9.585C4.75673 10.35 5.13744 11.0086 5.78887 11.4386C6.33887 11.8021 7.04887 11.9793 7.7853 11.9393C8.75887 11.8857 9.52244 11.5143 10.0553 10.8357C10.4596 10.32 10.7153 9.65143 10.8282 8.81C11.2924 9.09 11.6353 9.45786 11.826 9.90071C12.1489 10.6536 12.1674 11.8907 11.1582 12.8993C10.2739 13.7821 9.21102 14.165 7.60459 14.1764C5.82244 14.1636 4.47459 13.5914 3.59887 12.4779C2.77744 11.435 2.35387 9.92786 2.33816 8C2.35387 6.07143 2.77816 4.565 3.59887 3.52143C4.47459 2.40786 5.82244 1.83643 7.60459 1.82357C9.39959 1.83714 10.7703 2.41071 11.6803 3.53071C12.1267 4.07929 12.4624 4.76929 12.6846 5.57357L13.946 5.23786C13.6767 4.24714 13.2539 3.39357 12.6789 2.68571C11.5124 1.25 9.79816 0.515 7.6003 0.5C5.40673 0.515 3.71959 1.25357 2.58673 2.69357C1.57887 3.97571 1.05816 5.76929 1.04102 8.005C1.05816 10.2407 1.57887 12.0243 2.58673 13.3064C3.7203 14.7464 5.4153 15.485 7.60816 15.5C9.55887 15.4864 10.9332 14.9757 12.0653 13.845C13.5474 12.3643 13.5024 10.5093 13.0146 9.37071C12.6396 8.49571 11.8932 7.795 10.886 7.36357L10.8853 7.36286ZM7.7153 10.6179C6.89959 10.6636 6.05244 10.2979 6.0103 9.51357C5.97959 8.93214 6.42459 8.28357 7.7653 8.20643C8.37124 8.16575 8.9799 8.20756 9.57459 8.33071C9.4203 10.2607 8.51387 10.5736 7.7153 10.6179Z"
                fill="currentColor"
              />
            </svg>
          )}
          {network === EditorialFacebookPostNetwork.TWITTER && (
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.02361 0.699219H0.423197L5.69781 8.2185L0.346191 14.2992H2.27854L6.55014 9.44088L9.94671 14.2992H14.5775L9.1262 6.54032L14.2309 0.699219H12.2819L8.2537 5.29064L5.02361 0.699219ZM10.8142 12.8969L3.14543 2.14933H4.15687L11.8442 12.8969H10.8142Z"
                fill="currentColor"
              />
            </svg>
          )}
          {network === EditorialFacebookPostNetwork.LINKEDIN && (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_14496_83709)">
                <path
                  d="M13.8896 0.5H1.10742C0.495117 0.5 0 0.983398 0 1.58105V14.416C0 15.0137 0.495117 15.5 1.10742 15.5H13.8896C14.502 15.5 15 15.0137 15 14.4189V1.58105C15 0.983398 14.502 0.5 13.8896 0.5ZM4.4502 13.2822H2.22363V6.12207H4.4502V13.2822ZM3.33691 5.14648C2.62207 5.14648 2.04492 4.56934 2.04492 3.85742C2.04492 3.14551 2.62207 2.56836 3.33691 2.56836C4.04883 2.56836 4.62598 3.14551 4.62598 3.85742C4.62598 4.56641 4.04883 5.14648 3.33691 5.14648ZM12.7822 13.2822H10.5586V9.80176C10.5586 8.97266 10.5439 7.90332 9.40137 7.90332C8.24414 7.90332 8.06836 8.80859 8.06836 9.74316V13.2822H5.84766V6.12207H7.98047V7.10059H8.00977C8.30566 6.53809 9.03223 5.94336 10.1133 5.94336C12.3662 5.94336 12.7822 7.42578 12.7822 9.35352V13.2822Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_14496_83709">
                  <rect
                    width="15"
                    height="15"
                    fill="white"
                    transform="translate(0 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
          )}
          {network === EditorialFacebookPostNetwork.TIKTOK && (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_14496_85284)">
                <path
                  d="M10.6703 0.5H8.14238V10.7174C8.14238 11.9348 7.17012 12.9348 5.96016 12.9348C4.75021 12.9348 3.77793 11.9348 3.77793 10.7174C3.77793 9.52175 4.72861 8.54346 5.89536 8.5V5.93479C3.3242 5.97825 1.25 8.08696 1.25 10.7174C1.25 13.3696 3.36741 15.5 5.98178 15.5C8.59611 15.5 10.7135 13.3478 10.7135 10.7174V5.47825C11.6642 6.17392 12.8309 6.58696 14.0625 6.60871V4.04348C12.1612 3.97826 10.6703 2.41304 10.6703 0.5Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_14496_85284">
                  <rect
                    width="15"
                    height="15"
                    fill="white"
                    transform="translate(0 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
          )}
        </Inner>
        <Sep />
        <Inner>
          <Checkmark
            width="13"
            height="9"
            viewBox="0 0 13 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 1L4.66663 8L1 4.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Checkmark>
        </Inner>
      </Button>
    </HasPermission>
  );
};

export default AuthorizationButton;
